// ✅ src/pages/VehicleV2/JobCardEntryDetailReport.jsx
// UPDATED — Production BOM Listing design template
// Overflow fixed + Search Criteria in 3 columns + important list columns only

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/jobCardEntryReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const fmtDate = (v) => (v ? String(v) : "");

const normalizeOptionList = (
  arr,
  fallbackLabel = "Select",
  fallbackValue = "Select"
) => {
  const list = toList(arr).map((x) => {
    if (typeof x === "string" || typeof x === "number") {
      return { label: String(x), value: String(x) };
    }

    return {
      label: toStr(
        x.label ?? x.name ?? x.text ?? x.branchName ?? x.jobcardno ?? fallbackLabel,
        fallbackLabel
      ),
      value: String(x.value ?? x.id ?? x.key ?? x.branchId ?? x.jobcardno ?? fallbackValue),
      ...x,
    };
  });

  return list.length ? list : [{ label: fallbackLabel, value: fallbackValue }];
};

const createDefaultFilters = (branchType, branchId) => ({
  branchId: branchType === "HO" ? "Select" : String(branchId),
  fromDate: "",
  toDate: "",
  vehicleName: "",
  jobcardno: "Select",
  vehicleCode: "",
  vehicleRegNo: "",
  vehicleMainType: "Select",
});

export default function JobCardEntryDetailReport() {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     dropdown states
  =========================== */
  const [meta, setMeta] = useState({
    branches: [{ value: "Select", label: "Select" }],
    jobcards: [{ value: "Select", label: "Select" }],
    vehicleTypes: [{ value: "Select", label: "Select" }],
  });

  /* ===========================
     filters + list states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  /* ===========================
     computed
  =========================== */
  const totalRows = rows.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRows / pageSize)),
    [totalRows, pageSize]
  );

  const indexOfLastItem = page * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;

  const currentItems = useMemo(
    () => rows.slice(indexOfFirstItem, indexOfLastItem),
    [rows, indexOfFirstItem, indexOfLastItem]
  );

  const fieldClass = useMemo(
    () =>
      `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const tdClass = useMemo(
    () => `px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`,
    [isDark]
  );

  /* ===========================
     helpers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  /* ===========================
     meta loaders
  =========================== */
  const loadBranches = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/meta/branches`, {
      params: {
        branchType: session.branchType,
        branchId: session.branchId,
      },
      headers: commonHeaders,
    });

    return normalizeOptionList(data?.branches, "Select", "Select");
  }, [session.branchType, session.branchId, commonHeaders]);

  const loadVehicleTypes = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/meta/vehicle-types`, {
      headers: commonHeaders,
    });

    return normalizeOptionList(data?.vehicleTypes, "Select", "Select");
  }, [commonHeaders]);

  const loadJobcards = useCallback(
    async (branchIdToUse) => {
      const safeBranchId = isSelect(branchIdToUse)
        ? session.branchId
        : toStr(branchIdToUse, String(session.branchId));

      const { data } = await axios.get(`${API_BASE}/meta/jobcards`, {
        params: {
          branchId: safeBranchId,
        },
        headers: commonHeaders,
      });

      const list = toList(data?.jobcards);

      return normalizeOptionList(
        list.map((x) =>
          typeof x === "string" || typeof x === "number"
            ? { label: String(x), value: String(x) }
            : {
                label: toStr(x.label ?? x.jobcardno ?? x.name ?? "Select"),
                value: toStr(x.value ?? x.jobcardno ?? x.id ?? "Select"),
              }
        ),
        "Select",
        "Select"
      );
    },
    [session.branchId, commonHeaders]
  );

  const loadMeta = useCallback(
    async (branchIdForJobcards) => {
      try {
        setDropdownLoading(true);

        const [branches, vehicleTypes, jobcards] = await Promise.all([
          loadBranches(),
          loadVehicleTypes(),
          loadJobcards(branchIdForJobcards ?? filters.branchId ?? defaultFilters.branchId),
        ]);

        setMeta({
          branches,
          vehicleTypes,
          jobcards,
        });
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load dropdowns"
        );

        setMeta({
          branches: [{ value: "Select", label: "Select" }],
          vehicleTypes: [{ value: "Select", label: "Select" }],
          jobcards: [{ value: "Select", label: "Select" }],
        });
      } finally {
        setDropdownLoading(false);
      }
    },
    [loadBranches, loadVehicleTypes, loadJobcards, filters.branchId, defaultFilters.branchId]
  );

  /* ===========================
     list fetch
  =========================== */
  const fetchList = useCallback(
    async (nextFilters = filters) => {
      try {
        setListLoading(true);

        const params = {
          branchType: session.branchType,
          loginBranchId: session.branchId,
          branchId:
            session.branchType === "HO"
              ? nextFilters.branchId || "Select"
              : String(session.branchId),
          jobcardno: nextFilters.jobcardno || "Select",
          vehicleName: nextFilters.vehicleName || "",
          fromDate: nextFilters.fromDate || "",
          toDate: nextFilters.toDate || "",
          vehicleCode: nextFilters.vehicleCode || "",
          vehicleRegNo: nextFilters.vehicleRegNo || "",
          vehicleMainType: nextFilters.vehicleMainType || "Select",
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        const list = toList(data?.rows);
        setRows(list);
        setPage(1);
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load report"
        );
        setRows([]);
        setPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, session, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);

      setFilters(defaultFilters);
      setRows([]);
      setPage(1);

      await loadMeta(defaultFilters.branchId);
    } finally {
      setPageLoading(false);
    }
  }, [defaultFilters, loadMeta]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback(
    async (e) => {
      const { name, value } = e.target;

      if (name === "branchId") {
        const next = {
          ...filters,
          branchId: value,
          jobcardno: "Select",
        };

        setFilters(next);

        try {
          setDropdownLoading(true);
          const jobcards = await loadJobcards(value);
          setMeta((prev) => ({
            ...prev,
            jobcards,
          }));
        } catch (err) {
          console.error(err);
          toast.error(
            err?.response?.data?.message || err.message || "Failed to load jobcards"
          );
          setMeta((prev) => ({
            ...prev,
            jobcards: [{ value: "Select", label: "Select" }],
          }));
        } finally {
          setDropdownLoading(false);
        }

        return;
      }

      setField(name, value);
    },
    [filters, loadJobcards, setField]
  );

  const handleSearch = useCallback(async () => {
    await fetchList(filters);
    toast.success("Report loaded");
  }, [fetchList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    await loadMeta(reset.branchId);
    await fetchList(reset);
    toast.info("Showing all records");
  }, [session.branchType, session.branchId, loadMeta, fetchList]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    await fetchList(defaultFilters);
    toast.info("Page refreshed");
  }, [loadPage, fetchList, defaultFilters]);

  const handleReset = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    setRows([]);
    setPage(1);
    await loadMeta(reset.branchId);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId, loadMeta]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAllPrint = useCallback(() => {
    const params = new URLSearchParams({
      branchType: session.branchType,
      loginBranchId: String(session.branchId),
      branchId:
        session.branchType === "HO"
          ? filters.branchId
          : String(session.branchId),
      jobcardno: filters.jobcardno,
      vehicleName: filters.vehicleName,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      vehicleCode: filters.vehicleCode,
      vehicleRegNo: filters.vehicleRegNo,
      vehicleMainType: filters.vehicleMainType,
    });

    navigate(`/vehicle/JobCardEntryDetailsReport_AllPrint?${params.toString()}`);
    toast.info("Printing all job card records");
  }, [navigate, filters, session.branchType, session.branchId]);

  const handlePrintRow = useCallback(
    (row) => {
      const id =
        row?.fld_jobcardentrydetid ??
        row?.jobcardEntryDetId ??
        row?.id ??
        0;

      navigate(`/vehicle/JobCardEntryDetails_Print?id=${id}`);
    },
    [navigate]
  );

  const handleExport = useCallback(() => {
    try {
      if (!rows.length) {
        toast.info("No data to export");
        return;
      }

      const headers = [
        "S.No",
        "Vehicle Main Type",
        "Job Card No",
        "Entry Date",
        "Vehicle Code",
        "Vehicle Name",
        "Vehicle Reg No",
        "Location",
        "Hr/Km",
        "Status",
        "Nature Of Complaint",
        "Complaint Attended By",
        "Inspected By",
        "Remedy",
        "From Date",
        "In Time",
        "Out Time",
        "Hours",
        "Maintained By",
        "Spare Details",
      ];

      const lines = [headers.join(",")];

      rows.forEach((r, idx) => {
        const values = [
          idx + 1,
          toStr(r.fld_vehicletypename || r.VehicleType || ""),
          toStr(r.fld_jobcardno || r.jobCardNo || ""),
          toStr(r.fld_entrydate1 || r.entryDate || ""),
          toStr(r.fld_vehicle_Code || r.fld_vehicle_code || r.vehicleCode || ""),
          toStr(r.fld_vehicle_name || r.vehicleName || ""),
          toStr(r.fld_vehicle_regno || r.vehicleRegNo || ""),
          toStr(r.fld_worklocationname || r.location || ""),
          toStr(r.fld_hrs || r.hrKm || ""),
          toStr(r.fld_status || r.status || ""),
          toStr(r.fld_natureofcomplaint || r.complaint || ""),
          toStr(r.fld_complaintattendedby || r.attendedBy || ""),
          toStr(r.fld_inspectedby || r.inspectedBy || ""),
          toStr(r.fld_remedy || r.remedy || ""),
          toStr(r.fld_maintainfromdate1 || r.fromDate || ""),
          toStr(r.fld_maintainintime || ""),
          toStr(r.fld_maintainouttime || ""),
          toStr(r.fld_maintainhrs || ""),
          toStr(r.fld_maintenanceby || ""),
          toStr(r.fld_sparedetails || ""),
        ].map((x) => `"${String(x).replace(/"/g, '""')}"`);

        lines.push(values.join(","));
      });

      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "JobCardEntryDetailReport.csv";
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Exported CSV");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    }
  }, [rows]);

  const goPage = useCallback(
    (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      setPage(next);
    },
    [totalPages]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    (async () => {
      await loadPage();
      await fetchList(defaultFilters);
    })();
  }, [loadPage, fetchList, defaultFilters]);

  const statusText = pageLoading
    ? "Loading page..."
    : dropdownLoading
    ? "Loading dropdowns..."
    : listLoading
    ? "Searching..."
    : "Ready";

  return (
    <VehicleLayoutWrapper>
      <div
        className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-2xl md:text-3xl">
              JOB CARD ENTRY DETAIL REPORT
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>
                Job Card Entry Detail Report
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} • User ID: {session.loginUserId || "-"}
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button
                onClick={handleAllPrint}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                  isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaPrint />
                Print
              </button>

              <button
                onClick={handleExport}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport />
                Export
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                onClick={() => setShowFilters((p) => !p)}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleReset}
                    type="button"
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleViewAll}
                    type="button"
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    type="button"
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {listLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              {/* 3 columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branchId"
                    value={filters.branchId}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy || session.branchType !== "HO"}
                  >
                    {meta.branches.map((b, i) => (
                      <option key={`${b.value}-${i}`} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Job Card No</label>
                  <select
                    name="jobcardno"
                    value={filters.jobcardno}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {meta.jobcards.map((j, i) => (
                      <option key={`${j.value}-${i}`} value={j.value}>
                        {j.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Enter vehicle name"
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Enter vehicle code"
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input
                    type="text"
                    name="vehicleRegNo"
                    value={filters.vehicleRegNo}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Enter registration number"
                    disabled={isBusy}
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainType"
                    value={filters.vehicleMainType}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {meta.vehicleTypes.map((v, i) => (
                      <option key={`${v.value}-${i}`} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS SUMMARY ==================== */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              Total Records: {totalRows}
            </div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {statusText}
            </div>
          </div>

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || dropdownLoading || listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : currentItems.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No Job Card Records Found
                </div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  Try adjusting your search criteria or click View All.
                </p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Job Card No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Entry Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle Reg No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Nature Of Complaint</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Print</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {currentItems.map((r, idx) => {
                    const sno = indexOfFirstItem + idx + 1;
                    const status = r.fld_status || r.status || "";

                    return (
                      <tr
                        key={`${r.fld_jobcardentrydetid ?? r.id ?? "row"}-${sno}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={tdClass}>{sno}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {r.fld_jobcardno || r.jobCardNo || "N/A"}
                        </td>
                        <td className={tdClass}>{fmtDate(r.fld_entrydate1 || r.entryDate || "") || "-"}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {r.fld_vehicle_name || r.vehicleName || "-"}
                        </td>
                        <td className={tdClass}>{r.fld_vehicle_regno || r.vehicleRegNo || "-"}</td>
                        <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                          {status || "-"}
                        </td>
                        <td className={tdClass}>{r.fld_natureofcomplaint || r.complaint || "-"}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handlePrintRow(r)}
                            disabled={isBusy}
                            className={`p-2 rounded transition ${
                              isDark
                                ? "text-white hover:bg-gray-600"
                                : "text-gray-800 hover:bg-gray-200"
                            } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                            title="Print"
                            type="button"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          {totalRows > 0 && (
            <div className="mt-6 flex justify-center items-center gap-3 flex-wrap">
              <button
                className={`px-4 py-2 border rounded-lg font-semibold ${
                  isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } ${isBusy || page <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => goPage(page - 1)}
                disabled={isBusy || page <= 1}
                type="button"
              >
                Prev
              </button>

              <div
                className={`px-4 py-2 border rounded-lg ${
                  isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
                }`}
              >
                Page {page} / {totalPages}
              </div>

              <button
                className={`px-4 py-2 border rounded-lg font-semibold ${
                  isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } ${isBusy || page >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => goPage(page + 1)}
                disabled={isBusy || page >= totalPages}
                type="button"
              >
                Next
              </button>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}